import React, { useCallback, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { v4 as uuid } from 'uuid';
import { ScoreCodeCardItem } from './ScoreCodeCardItem';
import {
    BINDING_TOKENS,
    CardElement,
    CardTemplate,
    DESIGNER_CANVAS_H,
    DESIGNER_CANVAS_W,
    TextAlign,
    buildLiveScoringUrl,
    resolveBindings,
} from './scoreCardTypes';
import { ImageLibrary } from '../../../shared/Components/ImageLibrary';

// ─── Canvas size for the designer (px) ───────────────────────────────────────
// Source of truth is in scoreCardTypes.ts; aliased here for local readability.
const CANVAS_W = DESIGNER_CANVAS_W;
const CANVAS_H = DESIGNER_CANVAS_H;

// ─── Zoom constants ───────────────────────────────────────────────────────────
const MIN_ZOOM  = 0.25;
const MAX_ZOOM  = 3.0;
const ZOOM_STEP = 0.25;

interface CardTemplateDesignerProps {
    open: boolean;
    template: CardTemplate;
    /** First active registration used for live preview. May be empty object when none available. */
    previewRegistration: Record<string, unknown>;
    /** Event document for binding fallbacks (e.g. starting hole). */
    previewEventData?: Record<string, unknown>;
    roundValue: number;
    /**
     * Optional callback to upload an image file to blob storage.
     * Should resolve with the CDN URL of the uploaded image.
     * When omitted the raw base64 data URL is used (local-only).
     */
    onImageUpload?: (file: File) => Promise<string>;
    onSave: (template: CardTemplate) => void;
    onClose: () => void;
}

// ─── Drag state ───────────────────────────────────────────────────────────────

interface DragState {
    elementId: string;
    startMouseX: number;
    startMouseY: number;
    startX: number; // percent
    startY: number; // percent
}

// ─── Resize state ─────────────────────────────────────────────────────────────

interface ResizeState {
    elementId: string;
    startMouseX: number;
    startMouseY: number;
    startW: number; // percent
    startH: number; // percent
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function clamp(val: number, min: number, max: number) {
    return Math.max(min, Math.min(max, val));
}

// Convert a raw pixel delta to a percentage delta — no clamping here;
// clamping is applied to the final position/size at the call site.
function pxToPercX(px: number) { return (px / CANVAS_W) * 100; }
function pxToPercY(px: number) { return (px / CANVAS_H) * 100; }

// ─── Layer display name ───────────────────────────────────────────────────────

function elementDisplayName(el: CardElement, idx: number, allElements: CardElement[]): string {
    const typeLabel =
        el.type === 'text'   ? 'Text' :
        el.type === 'image'  ? 'Image' :
        'Live Scoring QR';

    // Count how many elements of this type appear before idx to get a 1-based number
    const sameTypeBefore = allElements.slice(0, idx).filter((e) => e.type === el.type).length;

    // If there's only one of this type, skip the number for cleanliness
    const totalOfType = allElements.filter((e) => e.type === el.type).length;
    if (totalOfType === 1) return typeLabel;

    return `${typeLabel} ${sameTypeBefore + 1}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CardTemplateDesigner: React.FC<CardTemplateDesignerProps> = ({
    open,
    template: initialTemplate,
    previewRegistration,
    previewEventData,
    roundValue,
    onImageUpload,
    onSave,
    onClose,
}) => {
    const [template, setTemplate] = useState<CardTemplate>(() =>
        JSON.parse(JSON.stringify(initialTemplate)),
    );
    const [selectedId, setSelectedId]   = useState<string | null>(null);
    const [showPreview, setShowPreview] = useState(false);
    const [uploadingIds, setUploadingIds] = useState<Set<string>>(new Set());
    const [zoom, setZoom]               = useState(1.0);

    // 'add' = inserting new element; string = element ID to replace
    const [libraryOpen, setLibraryOpen] = useState(false);
    const [libraryContext, setLibraryContext] = useState<'add' | string>('add');

    const openLibraryForAdd     = () => { setLibraryContext('add');   setLibraryOpen(true); };
    const openLibraryForReplace = (elementId: string) => { setLibraryContext(elementId); setLibraryOpen(true); };

    const canvasRef         = useRef<HTMLDivElement>(null);
    const dragRef           = useRef<DragState | null>(null);
    const resizeRef         = useRef<ResizeState | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    // zoomRef lets drag/resize closures read the current zoom without stale captures
    const zoomRef           = useRef(zoom);
    React.useEffect(() => { zoomRef.current = zoom; }, [zoom]);

    // ── Non-passive wheel listener for scroll-to-zoom ─────────────────────────
    // Depends on `open`: MUI Dialog children aren't in the DOM until the first
    // time open transitions to true, so the ref is null on the initial mount run.
    React.useEffect(() => {
        if (!open) return;
        const el = scrollContainerRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            setZoom((prev) =>
                Math.min(MAX_ZOOM, Math.max(MIN_ZOOM,
                    prev * (e.deltaY < 0 ? 1.1 : 0.9),
                )),
            );
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [open]);

    // ── Sync local state when dialog is reopened with a new template ──────────
    React.useEffect(() => {
        if (open) {
            setTemplate(JSON.parse(JSON.stringify(initialTemplate)));
            setSelectedId(null);
            setShowPreview(false);
            setZoom(1.0);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectedElement = template.elements.find((el) => el.id === selectedId) ?? null;

    // ── Element mutations ─────────────────────────────────────────────────────

    const updateElements = useCallback(
        (updater: (els: CardElement[]) => CardElement[]) => {
            setTemplate((prev) => ({ ...prev, elements: updater(prev.elements) }));
        },
        [],
    );

    const updateElement = useCallback(
        (id: string, patch: Partial<CardElement>) => {
            updateElements((els) =>
                els.map((el) => (el.id === id ? { ...el, ...patch } : el)),
            );
        },
        [updateElements],
    );

    const addElement = (partial: Omit<CardElement, 'id'>) => {
        const el: CardElement = { id: uuid(), ...partial };
        updateElements((els) => [...els, el]);
        setSelectedId(el.id);
    };

    const deleteSelected = () => {
        if (!selectedId) return;
        updateElements((els) => els.filter((el) => el.id !== selectedId));
        setSelectedId(null);
    };

    const handleLibrarySelect = ({ url }: { url: string; displayName: string }) => {
        if (libraryContext === 'add') {
            const newEl: CardElement = {
                id: uuid(),
                type: 'image',
                x: 5, y: 5, width: 40, height: 40,
                imageDataUrl: url,
            };
            updateElements((els) => [...els, newEl]);
            setSelectedId(newEl.id);
        } else {
            updateElement(libraryContext, { imageDataUrl: url });
        }
        setLibraryOpen(false);
    };

    // ── Add helpers ───────────────────────────────────────────────────────────

    const handleAddText = () =>
        addElement({
            type: 'text',
            x: 5,
            y: 5,
            width: 60,
            height: 15,
            content: 'Text',
            fontSize: 14,
            fontWeight: 'normal',
            color: '#000000',
            textAlign: 'left',
        });

    const handleAddQrCode = () =>
        addElement({ type: 'qrcode', x: 70, y: 55, width: 25, height: 35 });

    // ── Drag: mouse events on canvas ──────────────────────────────────────────

    const handleElementMouseDown = (
        e: React.MouseEvent<HTMLDivElement>,
        el: CardElement,
    ) => {
        e.stopPropagation();
        setSelectedId(el.id);
        dragRef.current = {
            elementId: el.id,
            startMouseX: e.clientX,
            startMouseY: e.clientY,
            startX: el.x,
            startY: el.y,
        };

        const onMove = (me: MouseEvent) => {
            if (!dragRef.current) return;
            // Divide by zoom so screen-space mouse delta maps to canvas-space delta
            const dx = (me.clientX - dragRef.current.startMouseX) / zoomRef.current;
            const dy = (me.clientY - dragRef.current.startMouseY) / zoomRef.current;
            updateElement(dragRef.current.elementId, {
                x: clamp(dragRef.current.startX + pxToPercX(dx), 0, 100),
                y: clamp(dragRef.current.startY + pxToPercY(dy), 0, 100),
            });
        };
        const onUp = () => {
            dragRef.current = null;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    // ── Resize handle mouse events ────────────────────────────────────────────

    const handleResizeMouseDown = (
        e: React.MouseEvent<HTMLDivElement>,
        el: CardElement,
    ) => {
        e.stopPropagation();
        resizeRef.current = {
            elementId: el.id,
            startMouseX: e.clientX,
            startMouseY: e.clientY,
            startW: el.width,
            startH: el.height,
        };

        const onMove = (me: MouseEvent) => {
            if (!resizeRef.current) return;
            const dx = (me.clientX - resizeRef.current.startMouseX) / zoomRef.current;
            const dy = (me.clientY - resizeRef.current.startMouseY) / zoomRef.current;
            updateElement(resizeRef.current.elementId, {
                width:  clamp(resizeRef.current.startW + pxToPercX(dx), 1, 100),
                height: clamp(resizeRef.current.startH + pxToPercY(dy), 1, 100),
            });
        };
        const onUp = () => {
            resizeRef.current = null;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    // ── Canvas click (deselect) ────────────────────────────────────────────────
    const handleCanvasClick = () => setSelectedId(null);

    // ── Property panel helpers ─────────────────────────────────────────────────

    const handleBindingInsert = (token: string) => {
        if (!selectedElement || selectedElement.type !== 'text') return;
        updateElement(selectedElement.id, {
            content: (selectedElement.content ?? '') + token,
        });
    };

    const handleImageReplace = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !selectedElement) return;
        const elementId = selectedElement.id;

        const reader = new FileReader();
        reader.onload = async (evt) => {
            const base64 = evt.target?.result as string;

            // Show base64 preview immediately
            updateElement(elementId, { imageDataUrl: base64 });

            if (onImageUpload) {
                setUploadingIds((prev) => new Set(prev).add(elementId));
                try {
                    const cdnUrl = await onImageUpload(file);
                    updateElement(elementId, { imageDataUrl: cdnUrl });
                } catch (err) {
                    console.error('Image replace upload failed — keeping local preview:', err);
                } finally {
                    setUploadingIds((prev) => { const s = new Set(prev); s.delete(elementId); return s; });
                }
            }
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <>
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth
            PaperProps={{ sx: { height: '90vh', maxHeight: '90vh', overflow: 'hidden' } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', py: 1.5 }}>
                <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
                    Score Code Card Template Designer
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex' }}>
                <Stack direction="row" sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>

                    {/* ── Left Panel: Element Palette ─────────────────────────── */}
                    <Stack
                        spacing={1.5}
                        sx={{
                            width: 200,
                            flexShrink: 0,
                            borderRight: '1px solid',
                            borderColor: 'divider',
                            p: 2,
                            overflowY: 'auto',
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            Add Elements
                        </Typography>

                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<TextFieldsIcon />}
                            onClick={handleAddText}
                            sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                        >
                            Text Field
                        </Button>

                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ImageOutlinedIcon />}
                            onClick={openLibraryForAdd}
                            sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                        >
                            Image Library
                        </Button>

                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<QrCode2Icon />}
                            onClick={handleAddQrCode}
                            sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                        >
                            Live Scoring QR Code
                        </Button>

                        <Divider />

                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            Background
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Box
                                component="label"
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    cursor: 'pointer',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 28, height: 28, borderRadius: 1,
                                        border: '1px solid rgba(0,0,0,0.25)',
                                        backgroundColor: template.backgroundColor,
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                                    {template.backgroundColor}
                                </Typography>
                                <input
                                    type="color"
                                    value={template.backgroundColor}
                                    onChange={(e) =>
                                        setTemplate((prev) => ({
                                            ...prev,
                                            backgroundColor: e.target.value,
                                        }))
                                    }
                                    style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                                />
                            </Box>
                        </Stack>

                        <Divider />

                        <Button
                            variant="text"
                            size="small"
                            onClick={() => setShowPreview((v) => !v)}
                            sx={{ textTransform: 'none' }}
                        >
                            {showPreview ? 'Edit Mode' : 'Preview Mode'}
                        </Button>
                    </Stack>

                    {/* ── Center Panel: Card Canvas ────────────────────────────── */}
                    <Stack sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', backgroundColor: 'grey.100' }}>

                        {/* Zoom control bar */}
                        <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                py: 0.75,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: 'background.paper',
                                flexShrink: 0,
                            }}
                        >
                            <Tooltip title="Zoom out (scroll wheel)">
                                <span>
                                    <IconButton
                                        size="small"
                                        disabled={zoom <= MIN_ZOOM}
                                        onClick={() => setZoom((z) => Math.max(MIN_ZOOM, parseFloat((z - ZOOM_STEP).toFixed(2))))}
                                    >
                                        <ZoomOutIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Typography
                                variant="caption"
                                sx={{ minWidth: 40, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
                            >
                                {Math.round(zoom * 100)}%
                            </Typography>
                            <Tooltip title="Zoom in (scroll wheel)">
                                <span>
                                    <IconButton
                                        size="small"
                                        disabled={zoom >= MAX_ZOOM}
                                        onClick={() => setZoom((z) => Math.min(MAX_ZOOM, parseFloat((z + ZOOM_STEP).toFixed(2))))}
                                    >
                                        <ZoomInIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Button
                                size="small"
                                onClick={() => setZoom(1.0)}
                                sx={{ textTransform: 'none', fontSize: 11, minWidth: 0, px: 1, color: 'text.secondary' }}
                            >
                                Reset
                            </Button>
                        </Stack>

                        {/* Scrollable canvas area */}
                        <Box
                            ref={scrollContainerRef}
                            sx={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'flex-start' }}
                        >
                            {showPreview ? (
                                <Stack alignItems="center" spacing={1} sx={{ p: 4 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Live Preview (using first registration data)
                                    </Typography>
                                    <ScoreCodeCardItem
                                        template={template}
                                        registration={previewRegistration}
                                        roundValue={roundValue}
                                        eventData={previewEventData}
                                        displayWidth={CANVAS_W}
                                        showBorder
                                    />
                                </Stack>
                            ) : (
                                // Outer div reserves the scaled footprint + surrounding padding so the
                                // scroll container always has breathing room on every side at any zoom level.
                                <Box
                                    sx={{
                                        width:  CANVAS_W * zoom + 80,
                                        height: CANVAS_H * zoom + 80,
                                        flexShrink: 0,
                                        position: 'relative',
                                        mx: 'auto',
                                    }}
                                >
                                    <Box
                                        ref={canvasRef}
                                        onMouseDown={handleCanvasClick}
                                        sx={{
                                            position: 'absolute',
                                            top: 40,
                                            left: 40,
                                            width: CANVAS_W,
                                            height: CANVAS_H,
                                            backgroundColor: template.backgroundColor,
                                            borderRadius: 1,
                                            boxShadow: 3,
                                            cursor: 'default',
                                            userSelect: 'none',
                                            transform: `scale(${zoom})`,
                                            transformOrigin: 'top left',
                                        }}
                                    >
                                        {template.elements.map((el) => {
                                            const isSelected = el.id === selectedId;
                                            const left   = (el.x      / 100) * CANVAS_W;
                                            const top    = (el.y      / 100) * CANVAS_H;
                                            const width  = (el.width  / 100) * CANVAS_W;
                                            const height = (el.height / 100) * CANVAS_H;

                                            return (
                                                <Box
                                                    key={el.id}
                                                    onMouseDown={(e) => handleElementMouseDown(e, el)}
                                                    sx={{
                                                        position: 'absolute',
                                                        left, top, width, height,
                                                        border: isSelected
                                                            ? '2px solid rgba(30,78,55,0.9)'
                                                            : '1px dashed rgba(0,0,0,0.2)',
                                                        cursor: 'move',
                                                        overflow: 'hidden',
                                                        boxSizing: 'border-box',
                                                    }}
                                                >
                                                    <CanvasElementPreview
                                                        element={el}
                                                        previewRegistration={previewRegistration}
                                                        previewEventData={previewEventData}
                                                        roundValue={roundValue}
                                                        width={width}
                                                        height={height}
                                                        uploading={uploadingIds.has(el.id)}
                                                    />
                                                    {/* Resize handle (bottom-right corner) */}
                                                    {isSelected && (
                                                        <Box
                                                            onMouseDown={(e) => handleResizeMouseDown(e, el)}
                                                            sx={{
                                                                position: 'absolute',
                                                                right: 0,
                                                                bottom: 0,
                                                                width: 10,
                                                                height: 10,
                                                                cursor: 'nwse-resize',
                                                                backgroundColor: 'rgba(30,78,55,0.9)',
                                                                borderTopLeftRadius: 2,
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* Canvas size info strip */}
                        <Stack
                            alignItems="center"
                            sx={{
                                py: 0.5,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: 'background.paper',
                                flexShrink: 0,
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                {CANVAS_W} × {CANVAS_H} px · 3.5″ × 2.5″ at print
                            </Typography>
                        </Stack>
                    </Stack>

                    {/* ── Right Panel: Layers | Properties (side by side) ─────── */}
                    <Stack
                        direction="row"
                        sx={{
                            width: 460,
                            flexShrink: 0,
                            borderLeft: '1px solid',
                            borderColor: 'divider',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Layers sub-column */}
                        <Stack
                            sx={{
                                width: 160,
                                flexShrink: 0,
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Stack sx={{ px: 2, pt: 2, pb: 0.5, flexShrink: 0 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                    Layers
                                </Typography>
                            </Stack>
                            <Stack sx={{ px: 1, pb: 1, flex: 1, overflowY: 'auto' }}>
                                {template.elements.length === 0 ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
                                        No elements yet.
                                    </Typography>
                                ) : (
                                    template.elements.map((el, idx) => {
                                        const isSelected = el.id === selectedId;
                                        const name = elementDisplayName(el, idx, template.elements);
                                        const Icon =
                                            el.type === 'text'   ? TextFieldsIcon :
                                            el.type === 'image'  ? ImageOutlinedIcon :
                                            QrCode2Icon;
                                        return (
                                            <Stack
                                                key={el.id}
                                                direction="row"
                                                spacing={0.75}
                                                alignItems="center"
                                                onClick={() => setSelectedId(el.id)}
                                                sx={{
                                                    px: 1,
                                                    py: 0.6,
                                                    borderRadius: 1,
                                                    cursor: 'pointer',
                                                    backgroundColor: isSelected
                                                        ? 'rgba(30,78,55,0.12)'
                                                        : 'transparent',
                                                    '&:hover': {
                                                        backgroundColor: isSelected
                                                            ? 'rgba(30,78,55,0.16)'
                                                            : 'rgba(0,0,0,0.04)',
                                                    },
                                                }}
                                            >
                                                <Icon
                                                    sx={{
                                                        fontSize: 15,
                                                        color: isSelected ? 'primary.main' : 'text.secondary',
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        flex: 1,
                                                        fontWeight: isSelected ? 700 : 400,
                                                        color: isSelected ? 'primary.main' : 'text.primary',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {name}
                                                </Typography>
                                            </Stack>
                                        );
                                    })
                                )}
                            </Stack>
                        </Stack>

                        {/* Properties sub-column */}
                        <Stack
                            spacing={1.5}
                            sx={{ flex: 1, overflowY: 'auto', p: 2 }}
                        >
                            {selectedElement ? (
                                <ElementPropertiesPanel
                                    key={selectedElement.id}
                                    element={selectedElement}
                                    previewRegistration={previewRegistration}
                                    roundValue={roundValue}
                                    uploading={uploadingIds.has(selectedElement.id)}
                                    onUpdate={(patch) => updateElement(selectedElement.id, patch)}
                                    onDelete={deleteSelected}
                                    onBindingInsert={handleBindingInsert}
                                    onImageReplace={handleImageReplace}
                                    onImageLibraryOpen={() => openLibraryForReplace(selectedElement.id)}
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
                                    Select a layer or click an element on the canvas to edit its properties.
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                </Stack>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 2, py: 1.5 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={uploadingIds.size > 0}
                    onClick={() => onSave(template)}
                    sx={{ textTransform: 'none', px: 3 }}
                    startIcon={uploadingIds.size > 0 ? <CircularProgress size={14} color="inherit" /> : undefined}
                >
                    {uploadingIds.size > 0 ? 'Uploading images…' : 'Save Template'}
                </Button>
            </DialogActions>
        </Dialog>

        <ImageLibrary
            open={libraryOpen}
            onClose={() => setLibraryOpen(false)}
            mode="select"
            onSelect={handleLibrarySelect}
        />
        </>
    );
};

// ─── Canvas element inline preview ───────────────────────────────────────────

function CanvasElementPreview({
    element,
    previewRegistration,
    previewEventData,
    roundValue,
    width,
    height,
    uploading = false,
}: {
    element: CardElement;
    previewRegistration: Record<string, unknown>;
    previewEventData?: Record<string, unknown>;
    roundValue: number;
    width: number;
    height: number;
    uploading?: boolean;
}) {
    if (element.type === 'text') {
        const resolved = resolveBindings(element.content ?? '', previewRegistration, roundValue, previewEventData);
        return (
            <Typography
                sx={{
                    fontFamily: 'Fredoka, sans-serif',
                    fontSize: element.fontSize ?? 12,
                    fontWeight: element.fontWeight ?? 'normal',
                    color: element.color ?? '#000000',
                    textAlign: element.textAlign ?? 'left',
                    lineHeight: 1.3,
                    px: 0.25,
                    wordBreak: 'break-word',
                    width: '100%',
                }}
            >
                {resolved || element.content || '(empty)'}
            </Typography>
        );
    }

    if (element.type === 'image') {
        if (!element.imageDataUrl) {
            return (
                <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100%' }}>
                    <ImageOutlinedIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                </Stack>
            );
        }
        return (
            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                <img
                    src={element.imageDataUrl}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', opacity: uploading ? 0.4 : 1 }}
                />
                {uploading && (
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{ position: 'absolute', inset: 0 }}
                    >
                        <CircularProgress size={16} />
                    </Stack>
                )}
            </Box>
        );
    }

    if (element.type === 'qrcode') {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.04)' }}>
                <QrCode2Icon sx={{ fontSize: Math.min(width, height) * 0.6, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontSize: 8, color: 'text.secondary', mt: 0.25 }}>
                    QR Code
                </Typography>
            </Stack>
        );
    }

    return null;
}

// ─── Deferred number field ────────────────────────────────────────────────────
// Holds a local string state so the user can type freely (empty, partial, etc.).
// The element is only updated on blur or Enter; invalid/out-of-range values are
// clamped or reverted to the last known good value at that point.

interface DeferredNumberFieldProps {
    label: string;
    /** Current value in display units (already converted from % if needed). */
    value: number;
    min?: number;
    max?: number;
    /** Called with the clamped display-unit value on commit (blur / Enter). */
    onCommit: (val: number) => void;
    sx?: object;
}

function DeferredNumberField({
    label,
    value,
    min = -Infinity,
    max = Infinity,
    onCommit,
    sx,
}: DeferredNumberFieldProps) {
    const [localValue, setLocalValue] = React.useState(String(Math.round(value)));
    const isFocused = React.useRef(false);

    // Sync from the external value whenever it changes and the field is not focused.
    // This handles canvas drags, element switches, and unit-mode changes.
    React.useEffect(() => {
        if (!isFocused.current) {
            setLocalValue(String(Math.round(value)));
        }
    }, [value]);

    const commit = React.useCallback(() => {
        const num = parseFloat(localValue);
        if (localValue.trim() === '' || isNaN(num)) {
            // Revert to current external value
            setLocalValue(String(Math.round(value)));
            return;
        }
        const clamped = clamp(num, min, max);
        setLocalValue(String(Math.round(clamped)));
        onCommit(clamped);
    }, [localValue, value, min, max, onCommit]);

    return (
        <TextField
            label={label}
            type="number"
            size="small"
            value={localValue}
            inputProps={{ min, max, step: 1 }}
            sx={sx}
            onChange={(e) => setLocalValue(e.target.value)}
            onFocus={() => { isFocused.current = true; }}
            onBlur={() => {
                isFocused.current = false;
                commit();
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    (e.currentTarget as HTMLInputElement).blur();
                }
            }}
        />
    );
}

// ─── Properties panel ────────────────────────────────────────────────────────

interface PropertiesPanelProps {
    element: CardElement;
    previewRegistration: Record<string, unknown>;
    roundValue: number;
    uploading?: boolean;
    onUpdate: (patch: Partial<CardElement>) => void;
    onDelete: () => void;
    onBindingInsert: (token: string) => void;
    onImageReplace: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onImageLibraryOpen: () => void;
}

function ElementPropertiesPanel({
    element,
    previewRegistration,
    roundValue,
    uploading = false,
    onUpdate,
    onDelete,
    onBindingInsert,
    onImageReplace,
    onImageLibraryOpen,
}: PropertiesPanelProps) {
    const [unitMode, setUnitMode] = React.useState<'%' | 'px'>('%');

    const typeLabel =
        element.type === 'text'   ? 'Text Field' :
        element.type === 'image'  ? 'Image'       :
        'Live Scoring QR';

    // Convert stored % value to display value in selected unit
    const toDisplay = (pct: number, axis: 'x' | 'y') => {
        if (unitMode === '%') return Math.round(pct);
        const dim = axis === 'x' ? CANVAS_W : CANVAS_H;
        return Math.round((pct / 100) * dim);
    };

    // Convert display input back to % for storage
    const fromInput = (val: number, axis: 'x' | 'y') => {
        if (unitMode === '%') return val;
        const dim = axis === 'x' ? CANVAS_W : CANVAS_H;
        return (val / dim) * 100;
    };

    const unitLabel = (axis: 'x' | 'y') => unitMode === '%' ? '%' : 'px';

    return (
        <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {typeLabel} Properties
                </Typography>
                <Tooltip title="Delete element">
                    <IconButton size="small" onClick={onDelete} color="error">
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Divider />

            {/* Position & size */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Position &amp; Size
                </Typography>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={unitMode}
                    onChange={(_, val) => { if (val) setUnitMode(val); }}
                    sx={{ height: 22 }}
                >
                    <ToggleButton value="%" sx={{ px: 1, py: 0, fontSize: 11, textTransform: 'none' }}>%</ToggleButton>
                    <ToggleButton value="px" sx={{ px: 1, py: 0, fontSize: 11, textTransform: 'none' }}>px</ToggleButton>
                </ToggleButtonGroup>
            </Stack>
            <Stack direction="row" spacing={1}>
                <DeferredNumberField
                    label={`X (${unitLabel('x')})`}
                    value={toDisplay(element.x, 'x')}
                    min={0}
                    max={unitMode === '%' ? 100 : CANVAS_W}
                    onCommit={(val) => onUpdate({ x: clamp(fromInput(val, 'x'), 0, 100) })}
                    sx={{ width: '50%' }}
                />
                <DeferredNumberField
                    label={`Y (${unitLabel('y')})`}
                    value={toDisplay(element.y, 'y')}
                    min={0}
                    max={unitMode === '%' ? 100 : CANVAS_H}
                    onCommit={(val) => onUpdate({ y: clamp(fromInput(val, 'y'), 0, 100) })}
                    sx={{ width: '50%' }}
                />
            </Stack>
            <Stack direction="row" spacing={1}>
                <DeferredNumberField
                    label={`W (${unitLabel('x')})`}
                    value={toDisplay(element.width, 'x')}
                    min={1}
                    max={unitMode === '%' ? 100 : CANVAS_W}
                    onCommit={(val) => onUpdate({ width: clamp(fromInput(val, 'x'), 0.5, 100) })}
                    sx={{ width: '50%' }}
                />
                <DeferredNumberField
                    label={`H (${unitLabel('y')})`}
                    value={toDisplay(element.height, 'y')}
                    min={1}
                    max={unitMode === '%' ? 100 : CANVAS_H}
                    onCommit={(val) => onUpdate({ height: clamp(fromInput(val, 'y'), 0.5, 100) })}
                    sx={{ width: '50%' }}
                />
            </Stack>

            <Divider />

            {/* Text-specific controls */}
            {element.type === 'text' && (
                <>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Content
                    </Typography>
                    <TextField
                        label="Text"
                        size="small"
                        multiline
                        minRows={2}
                        value={element.content ?? ''}
                        onChange={(e) => onUpdate({ content: e.target.value })}
                        fullWidth
                    />

                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Insert Data Binding
                    </Typography>
                    <Select
                        size="small"
                        displayEmpty
                        value=""
                        onChange={(e: SelectChangeEvent<string>) => {
                            if (e.target.value) onBindingInsert(e.target.value);
                        }}
                        renderValue={() => 'Insert field…'}
                    >
                        {BINDING_TOKENS.map(({ token, label }) => (
                            <MenuItem key={token} value={token}>
                                {label}
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    {token}
                                </Typography>
                            </MenuItem>
                        ))}
                    </Select>

                    <Divider />

                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Style
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            label="Size"
                            type="number"
                            size="small"
                            value={element.fontSize ?? 12}
                            onChange={(e) => onUpdate({ fontSize: Number(e.target.value) })}
                            inputProps={{ min: 6, max: 72 }}
                            sx={{ width: '45%' }}
                        />
                        <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
                            <Box
                                sx={{
                                    width: 24, height: 24, borderRadius: 0.5,
                                    border: '1px solid rgba(0,0,0,0.25)',
                                    backgroundColor: element.color ?? '#000000',
                                    flexShrink: 0,
                                }}
                            />
                            <Typography variant="caption">Color</Typography>
                            <input
                                type="color"
                                value={element.color ?? '#000000'}
                                onChange={(e) => onUpdate({ color: e.target.value })}
                                style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                            />
                        </Box>
                    </Stack>

                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={element.textAlign ?? 'left'}
                        onChange={(_, val: TextAlign | null) => {
                            if (val) onUpdate({ textAlign: val });
                        }}
                    >
                        <ToggleButton value="left"><FormatAlignLeftIcon fontSize="small" /></ToggleButton>
                        <ToggleButton value="center"><FormatAlignCenterIcon fontSize="small" /></ToggleButton>
                        <ToggleButton value="right"><FormatAlignRightIcon fontSize="small" /></ToggleButton>
                    </ToggleButtonGroup>

                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={element.fontWeight ?? 'normal'}
                        onChange={(_, val) => { if (val) onUpdate({ fontWeight: val }); }}
                    >
                        <ToggleButton value="normal" sx={{ fontSize: 12, textTransform: 'none' }}>Regular</ToggleButton>
                        <ToggleButton value="bold"   sx={{ fontSize: 12, fontWeight: 700, textTransform: 'none' }}>Bold</ToggleButton>
                    </ToggleButtonGroup>
                </>
            )}

            {/* Image-specific controls */}
            {element.type === 'image' && (
                <>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            Image
                        </Typography>
                        {uploading && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <CircularProgress size={10} />
                                <Typography variant="caption" color="text.secondary">Uploading…</Typography>
                            </Stack>
                        )}
                    </Stack>
                    {element.imageDataUrl && (
                        <Box sx={{ position: 'relative' }}>
                            <Box
                                component="img"
                                src={element.imageDataUrl}
                                alt="preview"
                                sx={{
                                    width: '100%', maxHeight: 80, objectFit: 'contain',
                                    borderRadius: 1, border: '1px solid', borderColor: 'divider',
                                    opacity: uploading ? 0.4 : 1,
                                }}
                            />
                            {uploading && (
                                <Stack alignItems="center" justifyContent="center"
                                    sx={{ position: 'absolute', inset: 0 }}>
                                    <CircularProgress size={18} />
                                </Stack>
                            )}
                        </Box>
                    )}
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            size="small"
                            component="label"
                            disabled={uploading}
                            startIcon={uploading ? <CircularProgress size={12} /> : <AddIcon />}
                            sx={{ textTransform: 'none', flex: 1 }}
                        >
                            {uploading ? 'Uploading…' : element.imageDataUrl ? 'Replace' : 'Upload'}
                            <input type="file" accept="image/*" hidden onChange={onImageReplace} />
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={uploading}
                            startIcon={<ImageOutlinedIcon fontSize="small" />}
                            onClick={onImageLibraryOpen}
                            sx={{ textTransform: 'none', flex: 1 }}
                        >
                            Library
                        </Button>
                    </Stack>
                </>
            )}

            {/* QR code info */}
            {element.type === 'qrcode' && (
                <>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        QR Code URL Pattern
                    </Typography>
                    <Typography variant="caption" sx={{ wordBreak: 'break-all', color: 'text.secondary', fontFamily: 'monospace', fontSize: 10 }}>
                        {buildLiveScoringUrl('{eventId}', '{scoreCode}')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        The event ID and score code are automatically embedded per card at print time.
                    </Typography>
                </>
            )}
        </Stack>
    );
}
